import {AfterViewInit, Component, inject, OnInit} from '@angular/core';
import {GraphicUtils} from '@aiknow-srl/aik-ngx-common';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs/operators';
import {Paths} from '../../utils/Paths';
import {BaseComponent} from "../BaseComponent";
import {MenuGroup} from "../../models/user/MenuGroup";
import {MenuVoice} from "../../models/user/MenuVoice";
import {baseZIndex} from 'devextreme/ui/overlay';
import {DxDrawerComponent} from "devextreme-angular";
import {EditModeEvent} from "../../events/EditModeEvent";
import {ApplicationOnlineStatusChangedNtf} from "../../events/notification/ApplicationOnlineStatusChangedNtf";

baseZIndex(2000);

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css']
})
export class HomeComponent extends BaseComponent implements OnInit, AfterViewInit {
  get loadingPage(): boolean {
    return this._loadingPage;
  }

  set loadingPage(value: boolean) {
    this._loadingPage = value;
    if (value)
      this.spinnerService.addHTTPRequest();
    else
      this.spinnerService.removeHTTPRequest();
    this.cdr.detectChanges();
  }

  //selectedItem MOVED IN GLOBAK MODEL SERVICE

  graphicUtils = GraphicUtils;

  private _loadingPage = true;

  undergoingMaintenance = false;
  selectedPageName: string = "";
  private _onlyIconSidenav = true;
  get onlyIconSidenav() { return this._onlyIconSidenav; }
  set onlyIconSidenav(value: boolean) {
    this._onlyIconSidenav = value;
    const badge = document.getElementById('hw-badge-host');
    if (badge) badge.style.visibility = 'hidden';
    setTimeout(() => {
      this.moveHeadwayBadge(value);
      if (badge) badge.style.visibility = '';
    }, value ? 250 : 75);
  }
  protected editMode = false;
  protected readonly window = window;
  private route: Router = inject(Router);
  private readonly activeRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly DEFAULT_MENU_GROUP = 'DASHBOARD';

  async navigateTo(item: MenuGroup, voice: MenuVoice, dontNavigate: boolean = false): Promise<void> {
    this.appService.menuVoice = structuredClone(voice);
    this.appService.menuGroup = structuredClone(item);
    if (!dontNavigate)
      this.route.navigateByUrl(`${Paths.HOME}/${voice.name}`).catch
      ((e) => {
        this.logger.error(`Error in goto, trying to go to unknown route [${voice.name}]`, e);
      });
  }

  async navigateToOutsideMenu(path: string, dontNavigate: boolean = false) {
    this.appService.menuGroup = undefined;
    this.appService.menuVoice = new MenuVoice();
    this.appService.menuVoice.name = path;
    if (!dontNavigate)
      this.route.navigateByUrl(`${Paths.HOME}/${path}`).catch
      ((e) => {
        this.logger.error(`Error in goto, trying to go to unknown route [${path}]`, e);
      });
  }

  private syncMenuFromRoute(childPath: string | undefined): void {
    const hasActiveChild = !!childPath && childPath !== Paths.EMPTY && childPath !== '**';
    if (hasActiveChild) {
      const matchedGroup = this.gmService.user.menuItems?.find(g =>
        g.voices?.some(v => v.name === childPath)
      );
      const matchedVoice = matchedGroup?.voices?.find(v => v.name === childPath);
      if (matchedGroup && matchedVoice) {
        this.navigateTo(matchedGroup, matchedVoice, true).then(() => { this.loadingPage = false; });
        matchedGroup.isOpen = true;
      } else {
        this.navigateToOutsideMenu(childPath!, true).then(() => { this.loadingPage = false; });
      }
    } else {
      const defaultGroup = this.defaultMenuGroup();
      if (!!defaultGroup) {
        this.loadingPage = true;
        const defaultVoice = this.defaultMenuVoice(defaultGroup);
        if (!!defaultVoice) {
          setTimeout(() => {
            this.navigateTo(defaultGroup, defaultVoice).then(() => { this.loadingPage = false; });
          });
        } else {
          this.logger.error('No menu for user found...');
        }
      } else {
        this.logger.error('No menu for user found...');
      }
    }
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.logger.debug(`${HomeComponent.name} initialized`);

    const activeChildPath = this.activeRoute.snapshot.firstChild?.routeConfig?.path;
    this.syncMenuFromRoute(activeChildPath);

    this.subscriptions = [
      this.route.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
        const childPath = this.activeRoute.snapshot.firstChild?.routeConfig?.path;
        this.syncMenuFromRoute(childPath);
      }),
      this.eventBus.onEvent(EditModeEvent.NAME, (event: EditModeEvent) => {
        this.editMode = event.isEditMode
      }),
      this.eventBus.onEvent(ApplicationOnlineStatusChangedNtf.NAME, (event: ApplicationOnlineStatusChangedNtf) => {
        this.undergoingMaintenance = !event.value.status;
      })
    ]

  }

  ngAfterViewInit(): void {
    const badge = document.createElement('div');
    badge.id = 'hw-badge-host';
    badge.classList.add('headway-widget-container');
    const slot = document.getElementById(this._onlyIconSidenav ? 'hw-slot-icon' : 'hw-slot-full');
    slot?.appendChild(badge);
    this.initHeadwayWidget();
  }

  private moveHeadwayBadge(onlyIcon: boolean) {
    const badge = document.getElementById('hw-badge-host');
    const slot = document.getElementById(onlyIcon ? 'hw-slot-icon' : 'hw-slot-full');
    if (badge && slot) slot.appendChild(badge);
  }

  toggleHeadway(event?: Event) {
    event?.stopPropagation();
    (window as any).Headway?.toggle();
  }

  private initHeadwayWidget() {
    const hw = (window as any).Headway;
    const hwConfig = (window as any).HW_config;
    if (hw && hwConfig) {
      hw.init(hwConfig);
    }
  }

  defaultMenuGroup() {
    if (!!this.gmService.user.menuItems) {
      const menuItem = this.gmService.user.menuItems.find(t => t.name === this.DEFAULT_MENU_GROUP);

      if (!!menuItem) {
        menuItem.isOpen = true;
        return menuItem;
      }
    }

    return undefined;
  }

  defaultMenuVoice(grp: MenuGroup) {
    if (!!grp.voices) {
      const voice = grp.voices[0];
      if (!!voice) {
        return voice;
      }
    }
    return undefined;
  }

  async clickAndClose(item: MenuGroup, voice: MenuVoice, drawer: DxDrawerComponent) {
    if (this.editMode) {
      return
    }
    if (this.appService.getRegistryEntityIsChanged()) {
      this.showDiscardEditsDialog().show().done((dialogResult: any) => {
        if (dialogResult) {
          this.loadingPage = true;
          this.appService.setRegistryEntityIsChanged(false);
          drawer.instance.hide().then(
            () => {
              item.isOpen = true;
              this.navigateTo(item, voice).then(
                () => {
                  this.loadingPage = false;
                });
            });
        } else {
          drawer.instance.hide()
        }
      });
    } else {
      this.loadingPage = true;
      drawer.instance.hide().then(
        () => {
          item.isOpen = true;
          this.navigateTo(item, voice).then(
            () => {
              this.loadingPage = false;
            });
        });
    }

  }

  async clickNoClose(item: MenuGroup, voice: MenuVoice) {
    if (this.appService.getRegistryEntityIsChanged()) {
      this.showDiscardEditsDialog().show().done((dialogResult: any) => {
        if (dialogResult) {
          this.loadingPage = true;
          setTimeout(() => {
            this.appService.setRegistryEntityIsChanged(false);
            item.isOpen = true;
            this.navigateTo(item, voice).then(
              () => {
                this.loadingPage = false;
              });
          }, 0);
        }
      });
    } else {
      this.loadingPage = true;
      this.cdr.detectChanges();
      setTimeout(() => {
        this.navigateTo(item, voice).then(
          () => {
            this.loadingPage = false;
          });
      }, 0);
    }
  }

  topbarGoTo($event: string) {
    if (this.appService.getRegistryEntityIsChanged()) {
      this.showDiscardEditsDialog().show().done((dialogResult: any) => {
        if (dialogResult) {
          this.loadingPage = true;
          this.appService.setRegistryEntityIsChanged(false);

          this.navigateToOutsideMenu($event).then(
            () => {
              this.loadingPage = false;
            });
        }
      });
    } else {
      this.loadingPage = true;
      this.navigateToOutsideMenu($event).then(
        () => {
          this.loadingPage = false;
        });
    }
  }

  toggleCurrentDrawer(fullDrawer: DxDrawerComponent, iconDrawer: DxDrawerComponent) {
    if (this.onlyIconSidenav && !this.appService.isSmallScreen()) {
      iconDrawer.instance.toggle();
    } else {
      fullDrawer.instance.toggle();
    }
  }

  dispatchSizeRefreshEvent() {
    window.dispatchEvent(new Event('resize'));
  }

  getIconName(iconName: any, isSelected: boolean) {
    if (isSelected)
      return 'vf-ic_fluent_' + iconName + '_24_filled'
    else
      return 'vr-ic_fluent_' + iconName + '_24_regular'
  }

  protected readonly sessionStorage = sessionStorage;
}
